import { useCallback, useEffect, useMemo, useRef } from 'react'

function useCallbackRef<T extends (...args: any[]) => any>(callback: T | undefined): T {
  const callbackRef = useRef(callback)

  useEffect(() => {
    callbackRef.current = callback
  })

  return useMemo(() => ((...args) => callbackRef.current?.(...args)) as T, [])
}

export type DebouncedCallback<T extends (...args: any[]) => any> = ((...args: Parameters<T>) => void) & {
  // Cancel any pending invocation. Used to drop a queued save when a newer one supersedes it.
  cancel: () => void
}

export interface DebouncedCallbackOptions {
  // When true, invoke on the leading edge of a burst (immediately on the first call) in
  // addition to the trailing edge. A single isolated call only fires once (leading only).
  // Defaults to false, i.e. trailing-edge only.
  leading?: boolean
}

export function useDebouncedCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay: number,
  options?: DebouncedCallbackOptions,
): DebouncedCallback<T> {
  const leading = options?.leading ?? false
  const handleCallback = useCallbackRef(callback)
  const debounceTimerRef = useRef<number | null>(null)

  const cancel = useCallback(() => {
    if (debounceTimerRef.current) {
      window.clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }
  }, [])

  useEffect(() => cancel, [cancel])

  const debounced = useCallback(
    (...args: Parameters<T>) => {
      // No active timer means this call starts a new burst. With leading enabled we fire
      // immediately so the first change is acted on right away (and only schedule a trailing
      // invocation if more calls arrive during the wait window).
      const isLeadingCall = leading && debounceTimerRef.current === null
      cancel()
      if (isLeadingCall) {
        handleCallback(...args)
      }
      debounceTimerRef.current = window.setTimeout(() => {
        debounceTimerRef.current = null
        if (!isLeadingCall) {
          handleCallback(...args)
        }
      }, delay)
    },
    [handleCallback, delay, cancel, leading],
  )

  return useMemo(() => {
    const fn = (...args: Parameters<T>) => debounced(...args)
    fn.cancel = cancel
    return fn as DebouncedCallback<T>
  }, [debounced, cancel])
}
